import { Link } from "react-router-dom";
import { useState } from "react";
import { useTournament, useTournamentUpdate } from "../TournamentContext";
import Button from "../../../components/Button";
import OpenRegistration from "./components/OpenRegistration";
import CloseRegistration from "./components/CloseRegistration";
import OpenTournament from "./components/OpenTournament";
import { doc, updateDoc } from "firebase/firestore";
import { firestore } from "../../../firebase/config";
import AddOrganizer from "./components/AddOrganizer";
import UpdatePhoto from "./components/UpdatePhoto";
import { DETAILS_DELAY } from "../../../constants";

const Details = () => {
  const { tournament, tournamentId, isUserOrganizer } = useTournament();
  const { updateTournament } = useTournamentUpdate();

  const {
    name,
    location,
    startDate,
    endDate,
    description,
    imageSrc,
    isRegistrationOpen,
    isTournamentPublic,
  } = tournament;

  const [localValues, setLocalValues] = useState({
    name,
    location,
    description,
  });

  const [debounceTimers, setDebounceTimers] = useState({});

  const updateTournamentField = async (field, value) => {
    const tournamentRef = doc(firestore, "tournaments", tournamentId);
    try {
      await updateDoc(tournamentRef, { [field]: value });
      updateTournament(field, value);
    } catch (error) {
      console.error("Error updating tournament:", error);
      alert("Something went wrong!");
    }
  };

  const handleImmediateChange = (field, value) => {
    updateTournamentField(field, value);
  };

  const handleDebouncedChange = (field, value) => {
    setLocalValues((prev) => ({ ...prev, [field]: value }));

    if (debounceTimers[field]) {
      clearTimeout(debounceTimers[field]);
    }

    const timer = setTimeout(() => {
      updateTournamentField(field, value);
      setDebounceTimers((prev) => ({ ...prev, [field]: null }));
    }, DETAILS_DELAY);

    setDebounceTimers((prev) => ({ ...prev, [field]: timer }));
  };

  const startDateText = new Date(startDate).toDateString();
  const endDateText = new Date(endDate).toDateString();

  return (
    <section className="w-full flex flex-col items-center text-center mt-2 mb-12">
      {isUserOrganizer && (
        <>
          <AddOrganizer tournamentId={tournamentId} />
          <UpdatePhoto tournamentId={tournamentId} currentImageSrc={imageSrc} />
        </>
      )}

      {!isTournamentPublic && <OpenTournament tournamentId={tournamentId} />}

      {isUserOrganizer ? (
        <textarea
          className="w-full max-w-4xl min-h-20 text-3xl font-extrabold text-stone-800 h-14 min-h-14 p-2 text-center bg-stone-100 mb-2 resize-y"
          onChange={(e) => handleDebouncedChange("name", e.target.value)}
          value={localValues.name}
        />
      ) : (
        <div className="w-full max-w-4xl mb-2 text-3xl font-extrabold text-stone-800 text-wrap break-words">
          {name}
        </div>
      )}

      {isUserOrganizer ? (
        <textarea
          className="w-full max-w-4xl min-h-20 text-2xl font-semibold text-stone-700 h-12 min-h-12 p-2 text-center bg-stone-100 mb-2 resize-y"
          onChange={(e) => handleDebouncedChange("location", e.target.value)}
          value={localValues.location}
        />
      ) : (
        <div className="w-full max-w-3xl mb-2 text-2xl font-semibold text-stone-700 text-wrap break-words">
          {location}
        </div>
      )}

      {isUserOrganizer ? (
        <div className="flex flex-wrap items-center justify-center">
          <input
            className="text-center text-xl font-semibold text-stone-600 text-center bg-stone-100 my-1"
            onChange={(e) => handleImmediateChange("startDate", e.target.value)}
            type="date"
            defaultValue={startDate}
          />
          <div className="">-</div>
          <input
            className="text-center text-xl font-semibold text-stone-600 text-center bg-stone-100 my-1"
            onChange={(e) => handleImmediateChange("endDate", e.target.value)}
            type="date"
            defaultValue={endDate}
          />
        </div>
      ) : (
        <p className="text-xl text-stone-600">
          {startDateText} - {endDateText}
        </p>
      )}

      {!tournament.groups && (
        <div className="flex flex-col items-center mb-4">
          {isUserOrganizer && (
            <>
              {isRegistrationOpen ? (
                <CloseRegistration tournamentId={tournamentId} />
              ) : (
                <OpenRegistration tournamentId={tournamentId} />
              )}
            </>
          )}

          {isRegistrationOpen && (
            <Link
              to={`/${tournamentId}/register`}
              className="flex items-center gap-1"
            >
              <Button color="indigo">Register</Button>
            </Link>
          )}
        </div>
      )}

      {isUserOrganizer ? (
        <textarea
          className="w-full max-w-6xl min-h-40 p-2 text-center text-lg text-gray-700 bg-stone-100 mb-8 resize-y"
          onChange={(e) => handleDebouncedChange("description", e.target.value)}
          value={localValues.description}
        />
      ) : (
        <p className="text-center text-lg text-gray-700 bg-stone-100 mb-8">
          {description}
        </p>
      )}

      {imageSrc && <img className="w-full max-w-xl" src={imageSrc} alt="" />}
    </section>
  );
};

export default Details;
